/* AOP Budget report — FY27 Plan vs FY26 Current Forecast by growth component
   (Base Business / On-Program Growth / Recompetes / New Business), ported
   from "New Report Request 2027 AOP_Budget.xlsx". Two tabs (Summary,
   Details); page chrome, filter bar and download icon are the same system
   Waterfall EAC / Monthly Report by Scenario (DFR Format) already use — this
   file is structurally a near-copy of MonthlyDFR.js, including its curated
   filter set (see AOP_FILTER_KEYS below — same 13 keys as DFR_FILTER_KEYS).
   Page styles and the filter bar are split into their own files (see
   ./reports/aop-budget/) to keep this file to state/behavior + layout. */
import React, { useState, useCallback, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import { applyCascadeDraftChange, pruneInvalidCascadeSelections } from "./filterRelationshipUtils";
import { usePlByProgramStyles, PlPageHeader } from "./pl-by-program/plByProgramTheme";
import { useAOPBudgetStyles } from "./aop-budget/aopBudgetPageStyles";
import { filtersForKeys, isOnlyDefaultYear } from "./aop-budget/aopBudgetFilterHelpers";
import AOPBudgetFilterBar from "./aop-budget/AOPBudgetFilterBar";
import Summary from "./aop-budget/Summary";
import Details from "./aop-budget/Details";
import Loading from "../../../common-components/Loading";
import {
  downloadAOPBudgetSummaryAPI,
  downloadAOPBudgetDetailsAPI,
} from "../../../api/reports-api";

const TABS = [
  { key: "summary", label: "Summary" },
  { key: "details", label: "Details" },
];

/** Monthly DFR's own DFR_FILTER_KEYS, minus Month/Quarter — this report has
 *  no month/quarter dimension (its figures are annual, next-fiscal-year
 *  totals per business type, not a monthly grid). */
const AOP_FILTER_KEYS = [
  "year",
  "contractType",
  "contractId",
  "clinLvl2",
  "orgId",
  "businessType",
  "awardType",
  "competitionType",
  "bu",
  "popEndDate",
  "projectId",
];

const TAB_FILTERS = {
  summary: AOP_FILTER_KEYS,
  details: AOP_FILTER_KEYS,
};

const DOWNLOAD_BY_TAB = {
  summary: { api: downloadAOPBudgetSummaryAPI, filename: "aop-budget-summary.xlsx" },
  details: { api: downloadAOPBudgetDetailsAPI, filename: "aop-budget-details.xlsx" },
};

function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default function AOPBudget() {
  const classes = useAOPBudgetStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(pruneInvalidCascadeSelections(appliedFilters, relationshipRows, visibleKeys));
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = filtersForKeys(visibleKeys).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) => applyCascadeDraftChange(prev, key, value, relationshipRows, visibleKeys));
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    const pruned = pruneInvalidCascadeSelections(draftFilters, relationshipRows, visibleKeys);
    const next = { ...initFilterValues() };
    (visibleKeys || []).forEach((k) => {
      next[k] = pruned[k];
    });
    setAppliedFilters(next);
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  // The Table 1 / Details version selection ("budget", "q1", "" for Live
  // EAC, ...) — owned here (not inside Summary.js) so it survives a tab
  // switch and Details.js's own fetch/downloads use the exact same value
  // Summary's dropdown last picked, per AOP_BUDGET_API.md's "Details ...
  // same selected version as Summary's Table 1."
  const [version, setVersion] = useState("");
  const [metaInfo, setMetaInfo] = useState({ currentYear: null, nextYear: null, nextYearVersion: null });
  const handleMeta = useCallback((patch) => setMetaInfo((prev) => ({ ...prev, ...patch })), []);

  const metaDate = [
    metaInfo.currentYear && metaInfo.nextYear ? `FY${metaInfo.nextYear} vs FY${metaInfo.currentYear}` : "",
    metaInfo.nextYearVersion?.name || "",
  ]
    .filter(Boolean)
    .join(" · ");

  const TAB_HEADER = {
    summary: {
      eyebrow: "AOP Budget",
      title: "Summary",
      accent: "Budget vs Current Forecast",
      badge: "Next FY",
      date: metaDate,
    },
    details: {
      eyebrow: "AOP Budget",
      title: "Details",
      accent: "Contract Detail",
      badge: "Next FY",
      date: metaDate,
    },
  };

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await entry.api(effectiveFilters, version);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("AOP Budget download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, version, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton onClick={() => goToReportsCatalog(history)} ariaLabel="Back to Reports" />
        <nav className={classes.crumb}>
          <span className={classes.crumbLink} onClick={() => goToReportsCatalog(history)}>
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>AOP Budget</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && <span className={classes.filterBadge}>{filterCount}</span>}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() => history.push(`/projects/reports/aop_budget/${tab.key}`)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <AOPBudgetFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={f.key === "year" && String(val) === String(new Date().getFullYear())}
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button className={classes.chipsClearAll} onClick={clearAllApplied} disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}>
            Clear All
          </button>
        </div>
      )}

      {/* ── Page header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
          date={TAB_HEADER[activeTab].date}
        />
      </div>

      {/* ── Content ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {activeTab === "summary" && (
            <Summary filters={effectiveFilters} version={version} onVersionChange={setVersion} onMeta={handleMeta} />
          )}
          {activeTab === "details" && (
            <Details filters={effectiveFilters} version={version} onMeta={handleMeta} />
          )}
        </div>
      </div>
    </div>
  );
}
